import { API, Auth, graphqlOperation } from "aws-amplify";
import { t } from "i18next";
import { toast } from "react-toastify";
import { setOrderContext, setCustomerContext, captureError, addBreadcrumb, clearUserContext } from "../sentry";

import config from "../aws-exports";
import { EFilterBy } from "../components/dashboard/Dashboard";
import {
  customListOrdersByCreatedAt,
  customListOrdersByDeliveryDate,
  customListOrdersById,
  customListOrdersByPrintManufacturer,
  customListOrdersByStatus,
  customListOrdersByUploadedDate,
  // @ts-expect-error type exists
} from "../graphql/customQueries";

import {
  createManufacturer,
  deleteOrderItem,
  updateManufacturer,
  updateOrderItem,
  // @ts-expect-error type exists
} from "../graphql/mutations";

import {
  getManufacturer,
  getOrderItem,
  listActivityLogByCreatedAt,
  listManufacturersByDate,
  // @ts-expect-error type exists
} from "../graphql/queries";
import {
  IOrderActivityLog,
  Manufacturer,
  ModelOrdersFilterInput,
  RemoteConfig,
  SortBy,
  UserData,
  UserOrder,
} from "../interfaces";
import { obfuscateEmail } from "../utils";

const catchError = (err: any, context?: { orderId?: string; operation?: string }) => {
  if (err !== "No current user" && err?.message !== "No current user") {
    captureError(err, {
      orderId: context?.orderId,
      extra: { 
        operation: context?.operation,
        status: err?.response?.status,
      },
    });
  }

  if (err.response) {
    if (err.response.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("refresh");
      toast.warn("You're unauthorized, please sign in");
    } else if (err.response.status === 403) {
      toast.warn("Unauthorized action");
    }
  } else if (err.message === "Network Error") {
    toast.error("Error occured, please try again");
  } else {
    if (err === "No current user") return;
    if (err.message) toast.error(err.message);
    else {
      if (typeof err === "string") {
        toast.error(err);
      }
    }
  }
};

// #region [Auth]
export const logout = async () => {
  try {
    await Auth.signOut();
    localStorage.clear();
    clearUserContext(); // Clear Sentry user context on logout
    return true;
  } catch (err) {
    catchError(err, { operation: 'logout' });
    return false;
  }
};

export const me = async (): Promise<UserOrder | any> => {
  try {
    const session = await Auth.currentSession();
    const currentUser = session.getIdToken().decodePayload();
    const groups = currentUser["cognito:groups"];

    const isManufacturer = groups.find(
      (group: string) => group === "manufacturer"
    );
    const isAdmin = groups.find((group: string) => group === "admin");

    const user: UserData = {
      id: currentUser.sub,
      email: currentUser.email,
      name: currentUser.name,
      isAdmin: !!isAdmin,
      isManufacturer: !!isManufacturer,
    };

    if (isManufacturer) {
      const res: any = await API.graphql(
        graphqlOperation(getManufacturer, { id: currentUser.name })
      );
      const { name }: Manufacturer = res?.data?.getManufacturer;
      user.manufacturerName = name;
    }

    setCustomerContext({
      id: user.id,
      email: user.email,
      name: user.name,
      isAdmin: user.isAdmin,
      isManufacturer: user.isManufacturer,
    });

    return user;
  } catch (err) {
    const pathname = window.location.pathname;
    const isDashboard = pathname.startsWith("/dashboard");
    const dashboardLogin = pathname.startsWith("/dashboard/login");
    if (isDashboard && !dashboardLogin) catchError(err, { operation: 'get_current_user' });
    return false;
  }
};

export const changePasswordApi = async ({
  username,
  password,
}: {
  username: string;
  password: string;
}) => {
  try {
    await API.post("cognitoApi", "/set-password", {
      body: { username, password, userPoolId: config.aws_user_pools_id },
    });
    return true;
  } catch (err) {
    console.log("err:", err);
    return false;
  }
};

export const getRemoteConfigApi = async (): Promise<RemoteConfig | null> => {
  try {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();

    const data = await API.post("manufacturerApi", `/?action=retrieve`, {
      body: { token },
    });
    return data as RemoteConfig;
  } catch (err) {
    catchError(err);
    return null;
  }
};

export const updateRemoteConfigApi = async (
  remoteConfig: RemoteConfig
): Promise<RemoteConfig | null> => {
  try {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();

    await API.post("manufacturerApi", "/?action=update", {
      body: {
        token,
        ...remoteConfig,
      },
    });

    return remoteConfig;
  } catch (err) {
    catchError(err);
    return null;
  }
};

export const updateProfile = async ({
  email,
  manufacturerName,
  remoteConfig,
  currentPassword,
  newPassword,
  user,
}: {
  email: string;
  manufacturerName: string;
  remoteConfig: RemoteConfig;
  currentPassword: string;
  newPassword?: string | null;
  user: UserData;
}) => {
  try {
    await Auth.signIn(user.email, currentPassword);

    const attributes: { name?: string; email?: string } = {};
    if (manufacturerName !== user.manufacturerName) {
      attributes.name = manufacturerName;
    }

    if (user.name && remoteConfig) {
      await updateRemoteConfigApi(remoteConfig);
    }

    const currentUser = await Auth.currentAuthenticatedUser();

    if (email !== user.email) {
      attributes.email = email;
      await Auth.updateUserAttributes(currentUser, { email });
    }

    if (newPassword && newPassword !== currentPassword) {
      Auth.changePassword(currentUser, currentPassword, newPassword);
    }

    if (Object.keys(attributes).length > 0) {
      API.graphql(
        graphqlOperation(updateManufacturer, {
          input: { id: user.name, ...attributes },
        })
      );
    }
    toast.success(t("profileUpdated").toString());
    return { email };
  } catch (err: any) {
    if (/Incorrect username or password/.test(err?.message)) {
      toast.error(t("incorrectPassword").toString());
    }
    return false;
  }
};

export const verifyEmailApi = async (username: string) => {
  try {
    await API.post("cognitoApi", "/verify-email", {
      body: { username, userPoolId: config.aws_user_pools_id },
    });
  } catch (err) {
    console.log("err:", err);
  }
};

// # region end

// # region [Order]

export const checkOrder = async (): Promise<UserOrder | any> => {
  try {
    const res = await callAPI({
      query: getOrderItem,
      variables: {},
    });
    if (res && res.getOrderItem) {
      return res.getOrderItem;
    }
  } catch (err: any) {
    if (err.response && err.response.data) {
      throw err.response.data;
    } else {
      throw { message: "orderNotFound" };
    }
  }
  return false;
};

/**
 * This method is used to get login token
 * @param {string} email - email of login user
 * @param {string} orderNumber - order id of login user
 * @returns {string} - token
 */
export const getLoginToken = async (email: string, orderNumber: string) => {
  try {
    const res = await callAPI(
      {
        orderId: orderNumber,
        email: email,
      },
      "/login"
    );
    if (res && res.token) {
      return res.token;
    }
  } catch (err: any) {
    if (err.response && err.response.data) {
      throw err.response.data;
    } else {
      throw { message: "orderNotFound" };
    }
  }
  return false;
};

export const deleteOrder = async (input: any): Promise<UserOrder | null> => {
  try {
    addBreadcrumb('Deleting order', 'order', { orderId: input.id });
    const res: any = await API.graphql(
      graphqlOperation(deleteOrderItem, { input })
    );
    return res?.data?.deleteOrderItem;
  } catch (err) {
    console.log("delete order err:", err);
    catchError(err, { orderId: input.id, operation: 'delete_order' });
    return null;
  }
};

export const updateOrderApi = async (input: any): Promise<UserOrder | null> => {
  try {
    setOrderContext(input.id, { status: input.status });
    addBreadcrumb('Updating order (admin)', 'order', { orderId: input.id, fields: Object.keys(input) });
    const res: any = await API.graphql(
      graphqlOperation(updateOrderItem, { input })
    );
    return res?.data?.updateOrderItem;
  } catch (err) {
    console.log("update order err:", err);
    catchError(err, { orderId: input.id, operation: 'update_order_admin' });
    return null;
  }
};

/**
 * This method is used to call graphql apis
 * @param {object} body
 * @returns Promise
 */
const callAPI = async (body: object, path: string = "/invoke") => {
  const token = localStorage.getItem("token");
  if (path !== "/login" && !token) {
    return null;
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  // Only attach optional headers when values exist to avoid signing `undefined`/`null`
  if (token) {
    headers["X-Custom-Auth"] = token;
  }
  const apiKey = import.meta.env.VITE_X_API_KEY;
  if (apiKey) {
    headers["X-API-KEY"] = apiKey;
  }

  // Use X-Custom-Auth for custom JWT token (not Authorization)
  // Authorization header is reserved for AWS SigV4 signing (IAM auth)
  return API.post("pixlipGraphqlApi", path, {
    method: "POST",
    headers,
    body,
  });
};

/**
 * This is called by customer to update order
 * @param  {any} input
 * @returns Promise
 */
export const updateOrderApiCustomer = async (
  input: any
): Promise<UserOrder | null> => {
  try {
    setOrderContext(input.id, { status: input.status });
    addBreadcrumb('Updating order (customer)', 'order', { orderId: input.id, fields: Object.keys(input) });
    const res = await callAPI({
      query: updateOrderItem,
      variables: {
        input,
      },
    });
    if (res && res.updateOrderItem) {
      return res.updateOrderItem;
    }
    return null;
  } catch (err) {
    console.log("update order err:", err);
    catchError(err, { orderId: input.id, operation: 'update_order_customer' });
  }
  return null;
};

type getOrdersType = {
  nextToken: string | null;
  orderNumber?: string | null;
  sortByOrder?: SortBy | null;
  limit?: number;
  filterBy: string;
  filter?: ModelOrdersFilterInput;
};

type OrdersDataResult = {
  items: UserOrder[];
  nextToken: string | null;
};

const DEFAULT_ORDERS_RESULT: OrdersDataResult = { items: [], nextToken: null };

export const getOrdersData = async ({
  nextToken,
  orderNumber,
  sortByOrder,
  limit = 16,
  filterBy,
  filter,
}: getOrdersType): Promise<OrdersDataResult> => {
  try {
    if (orderNumber) {
      const res: any = await API.graphql(
        graphqlOperation(getOrderItem, { id: orderNumber })
      );
      const order: UserOrder = res.data.getOrderItem;
      if (
        filter?.printManufacturer &&
        order &&
        filter?.printManufacturer.eq !== order.printManufacturer
      ) {
        return { items: [], nextToken: null };
      }
      return { items: order ? [order] : [], nextToken: null };
    } else if (filterBy === EFilterBy.byOrderId) {
      const res: any = await API.graphql(
        graphqlOperation(customListOrdersById, {
          typename: "order",
          limit: limit,
          sortDirection: sortByOrder,
          filter: filter,
          nextToken,
        })
      );
      return res?.data?.listOrderItemById ?? DEFAULT_ORDERS_RESULT;
    } else if (filterBy === EFilterBy.byStatus) {
      // Extract status from filter and pass as sort key condition
      const statusCondition = filter?.status;
      const filterWithoutStatus = { ...filter };
      delete filterWithoutStatus.status;
      
      const res: any = await API.graphql(
        graphqlOperation(customListOrdersByStatus, {
          typename: "order",
          status: statusCondition,
          limit: limit,
          sortDirection: sortByOrder,
          filter: Object.keys(filterWithoutStatus).length > 0 ? filterWithoutStatus : undefined,
          nextToken,
        })
      );
      return res?.data?.listOrderItemByStatus ?? DEFAULT_ORDERS_RESULT;
    } else if (filterBy === EFilterBy.byPrintManufacturer) {
      const res: any = await API.graphql(
        graphqlOperation(customListOrdersByPrintManufacturer, {
          typename: "order",
          limit: limit,
          sortDirection: sortByOrder,
          filter: filter,
          nextToken,
        })
      );
      return res?.data?.listOrderItemByPrintManufacturer ?? DEFAULT_ORDERS_RESULT;
    } else if (filterBy === EFilterBy.byDeliveryDate) {
      const res: any = await API.graphql(
        graphqlOperation(customListOrdersByDeliveryDate, {
          typename: "order",
          limit: limit,
          sortDirection: sortByOrder,
          filter: filter,
          nextToken,
        })
      );
      return res?.data?.listOrderItemByDeliveryDate ?? DEFAULT_ORDERS_RESULT;
    } else if (filterBy === EFilterBy.byUploadedDate) {
      const res: any = await API.graphql(
        graphqlOperation(customListOrdersByUploadedDate, {
          typename: "order",
          limit: limit,
          sortDirection: sortByOrder,
          filter: filter,
          nextToken,
        })
      );
      return res?.data?.listOrderItemByUploadedDate ?? DEFAULT_ORDERS_RESULT;
    } else if (filterBy === EFilterBy.byUploadedAndReuploaded) {
      // Make two parallel queries for Uploaded and ReUploaded statuses
      const [uploadedRes, reUploadedRes]: any[] = await Promise.all([
        API.graphql(
          graphqlOperation(customListOrdersByStatus, {
            typename: "order",
            status: { eq: "Uploaded" },
            limit: limit,
            sortDirection: sortByOrder,
            filter: filter,
            nextToken,
          })
        ),
        API.graphql(
          graphqlOperation(customListOrdersByStatus, {
            typename: "order",
            status: { eq: "ReUploaded" },
            limit: limit,
            sortDirection: sortByOrder,
            filter: filter,
            nextToken,
          })
        ),
      ]);

      const uploadedItems = uploadedRes?.data?.listOrderItemByStatus?.items ?? [];
      const reUploadedItems = reUploadedRes?.data?.listOrderItemByStatus?.items ?? [];
      
      // Merge and sort by createdAt (descending by default)
      const mergedItems = [...uploadedItems, ...reUploadedItems].sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return sortByOrder === "ASC" ? dateA - dateB : dateB - dateA;
      });

      return {
        items: mergedItems.slice(0, limit),
        nextToken: uploadedRes?.data?.listOrderItemByStatus?.nextToken || 
                   reUploadedRes?.data?.listOrderItemByStatus?.nextToken || null,
      };
    } else {
      const res: any = await API.graphql(
        graphqlOperation(customListOrdersByCreatedAt, {
          typename: "order",
          limit: limit,
          sortDirection: sortByOrder,
          filter: filter,
          nextToken,
        })
      );
      return res?.data?.listOrderItemByCreatedAt ?? DEFAULT_ORDERS_RESULT;
    }
  } catch (err) {
    catchError(err, { operation: 'get_orders_data' });
    return DEFAULT_ORDERS_RESULT;
  }
};

export const getManufacturers = async () => {
  try {
    const res: any = await API.graphql(
      graphqlOperation(listManufacturersByDate, {
        typename: "manufacturer",
        sortDirection: "ASC",
      })
    );
    const manufacturers = res?.data?.listManufacturersByDate.items;
    return manufacturers;
  } catch (err) {
    catchError(err, { operation: 'get_manufacturers' });
    return false;
  }
};

// Generate a password that meets Cognito policy requirements
const generateSecurePassword = (): string => {
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const special = '!@#$%^&*';
  const all = lowercase + uppercase + numbers + special;
  
  // Ensure at least one of each required character type
  let password = 
    lowercase[Math.floor(Math.random() * lowercase.length)] +
    uppercase[Math.floor(Math.random() * uppercase.length)] +
    numbers[Math.floor(Math.random() * numbers.length)] +
    special[Math.floor(Math.random() * special.length)];
  
  // Fill the rest randomly (minimum 8 chars total)
  for (let i = 0; i < 8; i++) {
    password += all[Math.floor(Math.random() * all.length)];
  }
  
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

export const createManufacturerApi = async ({
  email,
  name,
}: {
  email: string;
  name: string;
}) => {
  try {
    const res = await Auth.signUp({
      username: email,
      password: generateSecurePassword(),
      attributes: {
        email,
        name,
      },
    });

    await API.graphql(
      graphqlOperation(createManufacturer, {
        input: { id: name, name, email, typename: "manufacturer" },
      })
    );

    API.post("cognitoApi", "/add-to-group", {
      body: { username: res.userSub, userPoolId: config.aws_user_pools_id },
    });

    return true;
  } catch (err) {
    return err;
  }
};

export const getOrderApi = async (id: string) => {
  try {
    addBreadcrumb('Getting order', 'order', { orderId: id });
    const res: any = await API.graphql(graphqlOperation(getOrderItem, { id }));
    const order: UserOrder = res.data.getOrderItem;
    
    if (order) {
      setOrderContext(id, { status: order.status, printManufacturer: order.printManufacturer });
    }
    
    return order;
  } catch (err) {
    captureError(err, { orderId: id, extra: { operation: 'get_order' } });
    return false;
  }
};

/**
 * This method is to list down activity logs of selected order
 * @param { string } id order id
 * @returns { false | IOrderActivityLog[] } list of activity logs
 */
export const listActiveLogByOrderId = async (id: string) => {
  try {
    const res: any = await API.graphql(
      graphqlOperation(listActivityLogByCreatedAt, {
        typename: "activityLog",
        sortDirection: "DESC",
        filter: { orderItemActivityLogsId: { eq: id } },
      })
    );
    const order: IOrderActivityLog[] =
      res.data.listActivityLogByCreatedAt.items;
    return order;
  } catch (err) {
    console.log(err);
    captureError(err, { orderId: id, extra: { operation: 'list_activity_logs' } });
    return false;
  }
};

export const getOrderEmail = async (id: string) => {
  const order = await getOrderApi(id);

  if (order) return obfuscateEmail(order.email);

  return null;
};

// # region end
